<template>
  <div class="features-page">
    <!-- 顶部导航 -->
    <header class="header">
      <div class="header-left" @click="goBack">
        <span class="back-icon">←</span>
        <span class="header-title">{{ $t('admin.Features.line7_text') }}</span>
      </div>
      <button class="header-icon" @click="handleLogout">🚪</button>
    </header>

    <!-- 加载状态 -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner">⏳</div>
      <div class="loading-text">{{ $t('common.loading') }}</div>
    </div>

    <!-- 功能点列表 -->
    <div v-else class="features-list">
      <!-- 按模块分组显示 -->
      <div v-for="(features, module) in featuresByModule" :key="module" class="module-section">
        <div class="module-header">
          <span class="module-icon">{{ getModuleIcon(module) }}</span>
          <h3 class="module-title">{{ module }}</h3>
          <button class="btn-add" @click="openAddModal(module)">
            ➕ 添加功能点
          </button>
        </div>
        
        <div class="feature-cards">
          <div v-for="feature in features" :key="feature.id" class="feature-card">
            <div class="feature-header">
              <div class="feature-code">{{ feature.code }}</div>
              <span :class="['status-badge', feature.enabled ? 'status-on' : 'status-off']">
                {{ feature.enabled ? '已启用' : '已禁用' }}
              </span>
            </div>
            <div class="feature-name">{{ feature.name }}</div>
            <div class="feature-desc">{{ feature.description || '暂无描述' }}</div>
            <div class="feature-actions">
              <button class="btn-action" @click="openEditModal(feature)">
                编辑
              </button>
              <button class="btn-action" @click="toggleFeatureStatus(feature)">
                {{ feature.enabled ? '禁用' : '启用' }}
              </button>
              <button class="btn-action btn-delete" @click="deleteFeature(feature)">
                删除
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 空状态 -->
    <div v-if="!loading && Object.keys(featuresByModule).length === 0" class="empty-state">
      <div class="empty-icon">📭</div>
      <div class="empty-text">{{ $t('admin.Features.line59_text') }}</div>
    </div>

    <!-- 底部导航 -->
    <nav class="bottom-nav">
      <div class="nav-item" @click="goToPage('/admin/dashboard')">
        <div class="nav-icon">🏠</div>
        <div class="nav-label">{{ $t('common.home') }}</div>
      </div>
      <div class="nav-item" @click="goToPage('/admin/users')">
        <div class="nav-icon">👥</div>
        <div class="nav-label">{{ $t('admin.Features.line70_text') }}</div>
      </div>
      <div class="nav-item" @click="goToPage('/admin/roles')">
        <div class="nav-icon">🔐</div>
        <div class="nav-label">{{ $t('admin.Features.line74_text') }}</div>
      </div>
      <div class="nav-item active" @click="goToPage('/admin/features')">
        <div class="nav-icon">⚙️</div>
        <div class="nav-label">{{ $t('admin.Features.line78_text') }}</div>
      </div>
    </nav>

    <!-- 新增/编辑功能点弹窗 -->
    <div v-if="showAddModal || showEditModal" class="modal-overlay-global" @click="closeModal">
      <div class="modal" @click.stop>
        <div class="modal-header">
          <h3>{{ isEditMode ? '编辑功能点' : '新增功能点' }}</h3>
          <button class="modal-close" @click="closeModal">×</button>
        </div>
        <div class="modal-body">
          <div class="form-group" v-if="!isEditMode">
            <label>{{ $t('admin.Features.line91_text') }}</label>
            <div style="display: flex; gap: 8px;">
              <select v-model="selectedModulePrefix" class="form-input" style="flex: 1;" @change="onModulePrefixChange">
                <option value="">{{ $t('admin.Features.line94_text') }}</option>
                <option value="product">{{ $t('admin.Features.line95_text') }}</option>
                <option value="user">{{ $t('admin.Features.line96_text') }}</option>
                <option value="order">{{ $t('admin.Features.line97_text') }}</option>
                <option value="stall">{{ $t('admin.Features.line98_text') }}</option>
                <option value="delivery">{{ $t('admin.Features.line99_text') }}</option>
                <option value="finance">{{ $t('admin.Features.line100_text') }}</option>
                <option value="system">{{ $t('admin.Features.line101_text') }}</option>
              </select>
              <select v-model="selectedAction" class="form-input" style="flex: 1;" @change="onActionChange">
                <option value="">{{ $t('admin.Features.line104_text') }}</option>
                <option value="view">{{ $t('admin.Features.line105_text') }}</option>
                <option value="create">{{ $t('admin.Features.line106_text') }}</option>
                <option value="edit">{{ $t('admin.Features.line107_text') }}</option>
                <option value="delete">{{ $t('admin.Features.line108_text') }}</option>
                <option value="manage">{{ $t('admin.Features.line109_text') }}</option>
                <option value="audit">{{ $t('admin.Features.line110_text') }}</option>
                <option value="export">{{ $t('admin.Features.line111_text') }}</option>
                <option value="import">{{ $t('admin.Features.line112_text') }}</option>
              </select>
            </div>
          </div>
          <div class="form-group">
            <label>{{ $t('admin.Features.line117_text') }} <span class="required">*</span></label>
            <input
              v-model="formData.code"
              type="text"
              class="form-input"
              :disabled="isEditMode"
              :placeholder="$t('admin.Features.line123_placeholder')"
            />
          </div>
          <div class="form-group">
            <label>{{ $t('admin.Features.line127_text') }} <span class="required">*</span></label>
            <input
              v-model="formData.name"
              type="text"
              class="form-input"
              :placeholder="$t('admin.Features.line132_placeholder')"
            />
          </div>
          <div class="form-group">
            <label>{{ $t('admin.Features.line136_text') }} <span class="required">*</span></label>
            <select v-model="formData.module" class="form-input">
              <option value="">{{ $t('admin.Features.line138_text') }}</option>
              <option
                v-for="module in moduleList"
                :key="module"
                :value="module"
              >
                {{ module }}
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>{{ $t('admin.Features.line149_text') }}</label>
            <textarea
              v-model="formData.description"
              class="form-input form-textarea"
              rows="3"
              :placeholder="$t('admin.Features.line154_placeholder')"
            ></textarea>
          </div>
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="formData.enabled" />
              <span>{{ $t('admin.Features.line160_text') }}</span>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-cancel" @click="closeModal">{{ $t('common.cancel') }}</button>
          <button class="btn btn-primary" @click="handleSave" :disabled="submitting">
            {{ submitting ? '保存中...' : '保存' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { logout } from '@/utils/auth';
import axios from 'axios';
import AppConfig from '@/config';

const api = axios.create({
  baseURL: AppConfig.backendUrl + '/api',
  timeout: 30000
});

// 请求拦截器
api.interceptors.request.use(config => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export default {
  name: 'FeatureManagement',
  data() {
    return {
      loading: false,
      submitting: false,
      showAddModal: false,
      showEditModal: false,
      isEditMode: false,
      features: [],
      featuresByModule: {},
      selectedModulePrefix: '',
      selectedAction: '',
      formData: {
        id: null,
        code: '',
        name: '',
        module: '',
        description: '',
        enabled: true
      },
      moduleList: [
        '商品管理',
        '订单管理',
        '用户管理',
        '档口管理',
        '配送管理',
        '财务管理',
        '系统管理'
      ]
    };
  },
  computed: {
    ...mapGetters(['userName'])
  },
  async mounted() {
    await this.loadFeatures();
  },
  methods: {
    async handleLogout() {
      await logout();
    },
    
    // 模块前缀变化时
    onModulePrefixChange() {
      if (this.selectedModulePrefix && this.selectedAction) {
        this.formData.code = `${this.selectedModulePrefix}:${this.selectedAction}`;
      }
      // 根据模块自动填充名称
      this.updateFeatureName();
    },
    
    // 操作变化时
    onActionChange() {
      if (this.selectedModulePrefix && this.selectedAction) {
        this.formData.code = `${this.selectedModulePrefix}:${this.selectedAction}`;
      }
      // 根据操作自动填充名称
      this.updateFeatureName();
    },
    
    // 根据选择的模块和操作自动更新功能名称
    updateFeatureName() {
      if (!this.selectedModulePrefix || !this.selectedAction) {
        return;
      }
      
      // 模块名称映射
      const moduleNames = {
        product: '商品',
        user: '用户',
        order: '订单',
        stall: '档口',
        delivery: '配送',
        finance: '财务',
        system: '系统'
      };
      
      // 操作名称映射
      const actionNames = {
        view: '查看',
        create: '创建',
        edit: '编辑',
        delete: '删除',
        manage: '管理',
        audit: '审核',
        export: '导出',
        import: '导入'
      };
      
      const moduleName = moduleNames[this.selectedModulePrefix] || '';
      const actionName = actionNames[this.selectedAction] || '';
      
      if (moduleName && actionName) {
        // 模块名 + 操作名：商品查看、用户创建
        this.formData.name = `${moduleName}${actionName}`;
      }
    },
    
    async loadFeatures() {
      this.loading = true;
      try {
        const res = await api.get('/admin/features');
        this.features = res.data || [];
        
        // 按模块分组
        this.featuresByModule = this.features.reduce((acc, feature) => {
          const moduleKey = feature.module || '未分类';
          if (!acc[moduleKey]) {
            acc[moduleKey] = [];
          }
          acc[moduleKey].push(feature);
          return acc;
        }, {});
      } catch (error) {
        console.error('加载功能点失败:', error);
        this.showToast('加载失败：' + (error.response?.data?.message || error.message), 'error');
      } finally {
        this.loading = false;
      }
    },

    openAddModal(module) {
      this.isEditMode = false;
      this.selectedModulePrefix = '';
      this.selectedAction = '';
      this.formData = {
        id: null,
        code: '',
        name: '',
        module: module || '',
        description: '',
        enabled: true
      };
      this.showAddModal = true;
    },

    openEditModal(feature) {
      this.isEditMode = true;
      this.formData = { ...feature };
      this.showEditModal = true;
    },

    closeModal() {
      this.showAddModal = false;
      this.showEditModal = false;
    },

    async handleSave() {
      if (!this.formData.code || !this.formData.name || !this.formData.module) {
        this.showToast('请填写必填项', 'error');
        return;
      }

      this.submitting = true;
      try {
        if (this.isEditMode) {
          // 编辑模式
          await api.put(`/admin/features/${this.formData.id}`, {
            name: this.formData.name,
            description: this.formData.description,
            enabled: this.formData.enabled
          });
          this.showToast('更新成功', 'success');
        } else {
          // 新增模式
          await api.post('/admin/features', {
            code: this.formData.code,
            name: this.formData.name,
            module: this.formData.module,
            description: this.formData.description,
            enabled: this.formData.enabled
          });
          this.showToast('添加成功', 'success');
        }
        this.closeModal();
        await this.loadFeatures();
      } catch (error) {
        console.error('保存功能点失败:', error);
        
        // 友好的错误提示
        const errorMessage = error.response?.data?.message || error.message;
        if (errorMessage.includes('已存在')) {
          this.showToast('功能代码已存在，请使用其他代码', 'error');
        } else if (errorMessage.includes('不存在')) {
          this.showToast('功能点不存在', 'error');
        } else {
          this.showToast('保存失败：' + errorMessage, 'error');
        }
      } finally {
        this.submitting = false;
      }
    },

    async toggleFeatureStatus(feature) {
      try {
        await api.patch(`/admin/features/${feature.id}/toggle`);
        feature.enabled = !feature.enabled;
        this.showToast(feature.enabled ? '已启用' : '已禁用', 'success');
      } catch (error) {
        console.error('更新状态失败:', error);
        this.showToast('操作失败：' + (error.response?.data?.message || error.message), 'error');
      }
    },

    async deleteFeature(feature) {
      if (!confirm(`确定要删除功能点"${feature.name}"吗？`)) {
        return;
      }
      try {
        await api.delete(`/admin/features/${feature.id}`);
        const module = feature.module;
        this.features = this.features.filter(f => f.id !== feature.id);
        this.featuresByModule[module] = this.featuresByModule[module].filter(f => f.id !== feature.id);
        this.showToast('删除成功', 'success');
      } catch (error) {
        console.error('删除失败:', error);
        this.showToast('删除失败：' + (error.response?.data?.message || error.message), 'error');
      }
    },

    getModuleIcon(module) {
      const iconMap = {
        '商品管理': '📦',
        '订单管理': '📋',
        '用户管理': '👥',
        '档口管理': '🏪',
        '配送管理': '🚚',
        '财务管理': '💰',
        '系统管理': '⚙️'
      };
      return iconMap[module] || '📌';
    },

    goBack() {
      this.$router.back();
    },

    goToPage(path) {
      if (this.$route.path === path) {
        return;
      }
      this.$router.push(path).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('路由跳转失败:', err);
        }
      });
    },

    showToast(message, type = 'info') {
      const toast = document.createElement('div');
      toast.className = `toast toast-${type}`;
      toast.textContent = message;
      toast.style.cssText = `
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: ${type === 'error' ? 'rgba(255,77,79,0.9)' : type === 'success' ? 'rgba(52,199,89,0.9)' : 'rgba(0,0,0,0.7)'};
        color: #fff;
        padding: 12px 24px;
        border-radius: 8px;
        font-size: 14px;
        z-index: 9999;
      `;
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 2000);
    }
  }
};
</script>

<style scoped>
.features-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 16px 16px 140px; /* 增加底部间距，避免被底部导航遮挡 */
}

.header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  padding: calc(12px + env(safe-area-inset-top, 0px)) 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.back-icon {
  font-size: 20px;
}

.header-title {
  font-size: 18px;
  font-weight: 700;
}

.header-icon {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255,255,255,0.2);
  border: none;
  font-size: 18px;
  cursor: pointer;
  border-radius: 50%;
}

/* 加载状态 */
.loading-state {
  text-align: center;
  padding: 60px 16px;
}

.loading-spinner {
  font-size: 48px;
  margin-bottom: 16px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.loading-text {
  font-size: 16px;
  color: #999;
}

/* 功能点列表 */
.features-list {
  padding: 16px;
}

.module-section {
  margin-bottom: 24px;
}

.module-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 12px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.module-icon {
  font-size: 24px;
}

.module-title {
  flex: 1;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.btn-add {
  padding: 6px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}

/* 功能点卡片 */
.feature-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}

.feature-card {
  background: #fff;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.feature-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.feature-code {
  font-size: 12px;
  font-family: monospace;
  color: #667eea;
  font-weight: 600;
}

.feature-name {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 6px;
  color: #333;
}

.feature-desc {
  font-size: 13px;
  color: #666;
  margin-bottom: 12px;
  line-height: 1.5;
}

.feature-actions {
  display: flex;
  gap: 8px;
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
}

.status-badge {
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-on {
  background: #e6f4ff;
  color: #1890ff;
}

.status-off {
  background: #f5f5f5;
  color: #999;
}

.btn-action {
  padding: 4px 10px;
  background: #f0f0f0;
  color: #666;
  border: none;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
}

.btn-action.btn-delete {
  background: #fff2f0;
  color: #ff4d4f;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 60px 16px;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.empty-text {
  font-size: 16px;
  color: #999;
}

/* 底部导航 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  display: flex;
  border-top: 1px solid #e0e0e0;
  padding-bottom: env(safe-area-inset-bottom);
}

.nav-item {
  flex: 1;
  text-align: center;
  padding: 8px 0;
  cursor: pointer;
}

.nav-item.active {
  color: #667eea;
}

.nav-icon {
  font-size: 20px;
  margin-bottom: 2px;
}

.nav-label {
  font-size: 12px;
}

/* 弹窗 */
.modal-overlay-global {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  background: rgba(0,0,0,0.5) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 9999 !important;
}

.modal {
  background: #fff;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow: auto;
}

.modal-header {
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
}

.modal-body {
  padding: 16px;
}

.modal-footer {
  padding: 16px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn {
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  border: none;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel {
  background: #f0f0f0;
  color: #666;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.form-group {
  margin-bottom: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
}

.required {
  color: #ff4d4f;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 14px;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
}
</style>
